import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (authUser) => {
    if (!authUser) {
      setUser(null);
      return;
    }
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, picture, is_admin')
        .eq('id', authUser.id)
        .single();
      setUser({
        user_id: authUser.id,
        email: authUser.email,
        name: profile?.name || authUser.user_metadata?.name || '',
        picture: profile?.picture || null,
        is_admin: profile?.is_admin || false,
      });
    } catch {
      setUser({
        user_id: authUser.id,
        email: authUser.email,
        name: authUser.user_metadata?.name || '',
        is_admin: false,
      });
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await fetchProfile(session.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchProfile]);

  useEffect(() => {
    // Initial check
    checkAuth();

    // Listen for auth state changes.
    // IMPORTANT: never `await` Supabase calls directly inside this callback —
    // it deadlocks the internal auth lock and leaves `loading` stuck to true
    // (admin page would forever show the spinner). Defer async work via setTimeout.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(() => {
        if (event === 'SIGNED_IN' && session?.user) {
          fetchProfile(session.user);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, [checkAuth, fetchProfile]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
